import { withTableStyleDefaults } from './tableStyles';
import { withStructureRowKinds } from '../../../../shared/tables/tableRowKinds';

/**
 * Logique pure du bloc Tableau : normalisation des colonnes et des lignes,
 * type effectif d'une cellule, alignements, portée des bordures, et les deux
 * transpositions qu'exigent les dispositions non tabulaires.
 *
 * Le composant ne fait plus que dessiner ce que ces fonctions décident.
 */

/**
 * Dispositions possibles. `table` rend un `<table>`, `cards` une grille de
 * cartes — et c'est `cardHeader` qui décide de ce que la carte découpe.
 *
 * `rows` (lignes-paragraphes) et `compare` ont existé sur cette branche :
 * `resolveTableLayout` les ramène sur `cards`, la seconde en passant par
 * `cardHeader: 'columnTitle'`, qui rend exactement ce que rendait `compare`.
 */
export const TABLE_LAYOUTS = ['table', 'cards'];

/** Ce que coiffe une carte : la première cellule de la ligne, ou la colonne. */
export const TABLE_CARD_HEADERS = ['firstCell', 'columnTitle'];

/**
 * Types de cellule.
 *
 * `media` n'est plus proposé — il demandait de choisir image ou vidéo dans un
 * second temps, alors que c'est le type lui-même qui répond. Il reste lu : des
 * cellules l'ont déjà en base.
 */
export const CELL_TYPES = ['text', 'image', 'video', 'icon', 'media'];

/** Densités Mantine dérivées de `size` quand aucun espacement n'est imposé. */
const SIZE_SPACING = {
  small: { horizontal: 'xs', vertical: 'xs' },
  middle: { horizontal: 'sm', vertical: 'sm' },
  default: { horizontal: 'md', vertical: 'md' },
};

/**
 * Colonnes normalisées.
 *
 * `textAlign` / `verticalAlign` sont conservés : ils étaient perdus par
 * l'ancienne normalisation, si bien que l'alignement réglé sur une colonne
 * n'atteignait jamais le rendu.
 */
export function normalizeTableColumns(columns) {
  return (Array.isArray(columns) ? columns : []).map((col, index) => ({
    key: col?.key || String(col?.label || `col_${index + 1}`),
    label: col?.label ?? col?.title ?? col?.key ?? '',
    widthPercent: Number.isFinite(Number(col?.widthPercent)) && col?.widthPercent != null
      ? Number(col.widthPercent)
      : null,
    cellType: normalizeCellType(col?.cellType ?? col?.type),
    textAlign: col?.textAlign || '',
    verticalAlign: col?.verticalAlign || '',
  }));
}

/** Lignes normalisées : toujours autant de cellules que de colonnes. */
export function normalizeTableRows(rows, columnCount) {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const cells = Array.isArray(row?.cells) ? row.cells : [];
    return {
      ...row,
      cells: Array.from({ length: columnCount }, (_, i) => cells[i] || {}),
    };
  });
}

function normalizeCellType(value) {
  return CELL_TYPES.includes(value) ? value : 'text';
}

/**
 * Texte nu d'une cellule : au canvas, une valeur est du HTML. C'est sur ce
 * texte que se décident les fusions automatiques — une cellule qui ne contient
 * qu'un paragraphe vide est vide.
 */
export function plainCellText(value) {
  if (value == null) return '';
  if (typeof value !== 'string') return String(value);
  return value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/**
 * Libellé d'origine de chaque colonne, par clé : les bornes d'une fusion et
 * les colonnes d'une structure le visent, pas le renommage fait au canvas.
 */
export function columnOriginLabels(columns, normalizedColumns) {
  return new Map(
    normalizedColumns.map((col, idx) => [col.key, columns[idx]?.originalLabel ?? plainCellText(col.label)]),
  );
}

/**
 * Lignes en texte nu, rangées par clé de colonne, et typées par la structure
 * de la table quand elle en a une : l'API ne type ni les titres de poste ni
 * la TVA (cf. withStructureRowKinds).
 */
export function typedRowTexts(rows, normalizedColumns, structure, originLabels) {
  const entries = rows.map((row) => {
    const entry = { key: row.key, rowKind: row.rowKind };
    normalizedColumns.forEach((col, idx) => {
      entry[col.key] = plainCellText(row.cells?.[idx]?.value);
    });
    return entry;
  });
  if (!structure) return entries;
  return withStructureRowKinds(entries, normalizedColumns, structure, {
    columnLabel: (col) => originLabels.get(col.key),
  });
}

/**
 * Type effectif d'une cellule : la cellule l'emporte sur sa colonne.
 * Une cellule sans type suit la colonne — c'est le cas de tout ce qui vient
 * du parser, qui ne type que les colonnes.
 */
export function resolveCellType(cell, column) {
  if (cell?.cellType) return normalizeCellType(cell.cellType);
  return normalizeCellType(column?.cellType);
}

/** `start`/`end` (stockés) → valeurs CSS. */
export function resolveTextAlign(cell, column, fallback = 'left') {
  const value = cell?.textAlign || column?.textAlign;
  if (!value) return fallback;
  if (value === 'start') return 'left';
  if (value === 'end') return 'right';
  return value;
}

export function resolveVerticalAlign(cell, column, fallback = 'middle') {
  const value = cell?.verticalAlign || column?.verticalAlign;
  if (!value) return fallback;
  if (value === 'center') return 'middle';
  if (value === 'start') return 'top';
  if (value === 'end') return 'bottom';
  return value;
}

/**
 * Portée effective des bordures.
 *
 * `bordered` portait à lui seul « pas de bordures » avant l'existence de
 * `borderScope` : un tableau déjà en base doit continuer de se rendre sans
 * bordures. `withTableStyleDefaults` fait la déduction, on ne la refait pas
 * ici.
 */
export function resolveBorderScope(design) {
  const { borderScope, bordered } = withTableStyleDefaults(design);
  if (bordered === false && borderScope === 'all') return 'none';
  return borderScope;
}

/** Espacement Mantine : réglage explicite, sinon dérivé de la densité. */
export function resolveTableSpacing(design) {
  const { size, horizontalSpacing, verticalSpacing } = design || {};
  const derived = SIZE_SPACING[size] || SIZE_SPACING.default;
  return {
    horizontal: horizontalSpacing || derived.horizontal,
    vertical: verticalSpacing || derived.vertical,
  };
}

/**
 * Une ligne vue comme une suite de couples libellé / cellule — ce que les
 * dispositions `rows` et `cards` affichent, et ce en quoi un tableau se replie
 * sur mobile.
 *
 * `firstColumnEmphasis` détache la première cellule : elle devient le titre du
 * bloc, et ne fait plus partie des couples.
 */
export function toRowEntries(row, columns, { firstColumnEmphasis = false } = {}) {
  const entries = columns.map((column, index) => ({
    column,
    columnIndex: index,
    cell: row.cells[index] || {},
  }));
  if (!firstColumnEmphasis || !entries.length) {
    return { title: null, entries };
  }
  return { title: entries[0], entries: entries.slice(1) };
}

/**
 * Transposition : une carte par colonne — la disposition où les colonnes
 * deviennent l'axe principal.
 *
 * `firstColumnEmphasis` décide du sort de la première colonne :
 *  - **désactivé (défaut)** : chaque colonne a sa carte, titre de colonne
 *    compris. C'est ce qu'on attend en voyant « une carte par colonne » ;
 *  - **activé** : la première colonne ne devient pas une carte, elle fournit
 *    les libellés de ligne à l'intérieur des autres — la grille tarifaire, qui
 *    se lit offre par offre. Il faut alors au moins deux colonnes, sinon il ne
 *    reste rien à afficher : on rend `null` et l'appelant retombe ailleurs.
 */
export function toCompareCards(columns, rows, { firstColumnEmphasis = false } = {}) {
  if (!columns.length) return null;
  if (!firstColumnEmphasis) {
    return columns.map((column, columnIndex) => ({
      column,
      columnIndex,
      entries: rows.map((row, rowIndex) => ({
        rowIndex,
        row,
        labelColumn: null,
        labelCell: null,
        cell: row.cells[columnIndex] || {},
      })),
    }));
  }

  if (columns.length < 2) return null;
  const [labelColumn, ...valueColumns] = columns;
  return valueColumns.map((column, offset) => {
    const columnIndex = offset + 1;
    return {
      column,
      columnIndex,
      entries: rows.map((row, rowIndex) => ({
        rowIndex,
        row,
        labelColumn,
        labelCell: row.cells[0] || {},
        cell: row.cells[columnIndex] || {},
      })),
    };
  });
}

/**
 * Disposition effective. Une disposition en cartes qui n'a pas de quoi se
 * dessiner retombe sur le tableau plutôt que de ne rien rendre.
 */
export function resolveTableLayout(design, columns) {
  const { layout } = withTableStyleDefaults(design);
  // Dispositions retirées : `rows` rendait mal, `compare` est devenu une carte
  // par colonne. Les deux se lisent encore dans des blocs enregistrés.
  const normalized = layout === 'rows' || layout === 'compare' ? 'cards' : layout;
  if (!TABLE_LAYOUTS.includes(normalized)) return 'table';
  if (normalized !== 'table' && !columns.length) return 'table';
  return normalized;
}

/**
 * En-tête des cartes. `columnTitle` transpose : une carte par colonne, la
 * première colonne fournissant les libellés de ligne — il faut donc au moins
 * deux colonnes, sinon il ne reste rien à mettre dans la carte.
 */
export function resolveCardHeader(design, columns) {
  const resolved = withTableStyleDefaults(design);
  // Un bloc enregistré en `compare` n'a pas de `cardHeader` : sa disposition
  // dit déjà lequel c'était.
  const header = design?.cardHeader
    ?? (design?.layout === 'compare' ? 'columnTitle' : resolved.cardHeader);
  if (!TABLE_CARD_HEADERS.includes(header)) return 'firstCell';
  // Une seule colonne ne pose problème que si la première sert de libellés :
  // il ne resterait aucune carte.
  if (header === 'columnTitle' && resolved.firstColumnEmphasis && columns.length < 2) {
    return 'firstCell';
  }
  return header;
}

/** Part maximale de la largeur visible que prennent les colonnes figées. */
const STICKY_ZONE_MAX_SHARE = 0.5;

/**
 * Largeurs en px des colonnes d'un tableau qui défile, pour une largeur
 * visible `containerWidth`.
 *
 * Chaque colonne garde sa part (`widthPercent`, ou une part égale du reste),
 * sans descendre sous `minWidth` : c'est le tableau qui s'élargit et défile,
 * plutôt qu'une colonne étroite qui coupe ses mots lettre par lettre. Quand
 * tout tient, le résultat redonne exactement les pourcentages.
 *
 * Les `stickyCount` premières colonnes (figées) ne prennent jamais plus de la
 * moitié de la largeur visible, plancher compris : au-delà, il ne reste rien à
 * faire défiler sur un téléphone.
 *
 * @returns {number[]|null} null sans largeur mesurée.
 */
export function resolveColumnPixelWidths(columns, containerWidth, { minWidth = 0, stickyCount = 0 } = {}) {
  if (!containerWidth || !columns.length) return null;
  const declared = columns.reduce((sum, col) => sum + (col.widthPercent ?? 0), 0);
  const freeCount = columns.filter((col) => col.widthPercent == null).length;
  const freeShare = freeCount ? Math.max(100 - declared, 0) / freeCount : 0;
  const shares = columns.map((col) => col.widthPercent ?? freeShare);
  const totalShare = shares.reduce((sum, share) => sum + share, 0);
  // Des pourcentages qui ne font pas 100 : le navigateur les ramène à la
  // largeur du tableau, on fait de même.
  const widths = shares.map((share) => {
    const natural = totalShare > 0 ? (share / totalShare) * containerWidth : containerWidth / columns.length;
    return Math.max(natural, minWidth);
  });

  const stickyTotal = widths.slice(0, stickyCount).reduce((sum, width) => sum + width, 0);
  const stickyMax = containerWidth * STICKY_ZONE_MAX_SHARE;
  if (stickyCount && stickyTotal > stickyMax) {
    const ratio = stickyMax / stickyTotal;
    for (let index = 0; index < stickyCount; index += 1) {
      widths[index] = Math.max(widths[index] * ratio, minWidth);
    }
  }
  return widths.map((width) => Math.round(width * 10) / 10);
}
